"use client";

import { Facebook, Camera, ExternalLink, MapPin, Mail } from "lucide-react";
import { useLanguage } from "@/contexts/LanguageContext";
import { motion } from "framer-motion";

// Icon mapping for Footer component
const footerIconMap = {
  "Facebook": Facebook,
  "Camera": Camera,
  "MapPin": MapPin,
  "Mail": Mail,
};

const Footer = () => {
  const { t, getFooterSocialLinks } = useLanguage();
  const currentYear = new Date().getFullYear();
  const socialLinks = getFooterSocialLinks();

  // HTML Sitemap links for SEO
  const sitemapLinks = [
    { id: "hero", label: "Home" },
    { id: "commercial-gallery", label: "Commercial Photography" },
    { id: "gallery", label: "Photo Art Gallery" },
    { id: "process", label: "Process" },
    { id: "about", label: "About & Excellence" },
    { id: "contact", label: "Contact" },
  ];

  return (
    <footer className="border-t border-border pt-12 pb-8 bg-background theme-transition" role="contentinfo">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        {/* Social Media Section */}
        <div className="mb-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="text-center mb-8"
          >
            <h2 className="font-inter font-bold text-xl text-foreground mb-3">
              {t('footer.connectWithMe')}
            </h2>
            <p className="font-inter font-normal text-base text-foreground/80 max-w-2xl mx-auto">
              {t('footer.socialDescription')}
            </p>
          </motion.div>

          {/* Social Links - Fixed ARIA roles */}
          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.2 }}
            className="flex flex-wrap justify-center gap-6 mb-8"
            role="region"
            aria-label="Social media links"
          >
            {socialLinks.map((link, index) => {
              const Icon = footerIconMap[link.iconName as keyof typeof footerIconMap] || Camera;
              return (
                <motion.a
                  key={link.name}
                  href={link.url}
                  target="_blank"
                  rel="noopener noreferrer nofollow"
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.4, delay: index * 0.1 }}
                  className="group relative flex items-center bg-card border border-border hover:border-primary/50 px-6 py-4 rounded-lg transition-all duration-300 hover:shadow-lg hover:shadow-primary/10 min-w-[220px] min-h-[72px] theme-transition"
                  style={{ 
                    '--hover-color': link.color,
                  } as React.CSSProperties}
                  aria-label={`Visit ${link.name} profile`}
                >
                  <div className="flex items-center justify-center w-12 h-12 rounded-lg bg-gradient-to-br from-background to-card border border-border group-hover:border-[var(--hover-color)]/50 mr-4 transition-colors duration-300">
                    <Icon className="w-6 h-6 text-foreground group-hover:text-[var(--hover-color)] transition-colors duration-300" aria-hidden="true" />
                  </div>
                  
                  <div className="text-left">
                    <span className="font-inter font-bold text-base text-foreground group-hover:text-[var(--hover-color)] transition-colors duration-300">
                      {link.name}
                    </span>
                    <div className="flex items-center mt-1">
                      <span className="font-inter font-normal text-sm text-foreground/80 mr-2">
                        {link.name === "Facebook" ? t('footer.facebook.visit') : t('footer.flickr.visit')}
                      </span>
                      <ExternalLink className="w-3 h-3 text-primary" aria-hidden="true" />
                    </div>
                  </div>

                  {/* Hover Effect */}
                  <div className="absolute inset-0 rounded-lg bg-gradient-to-r from-transparent via-[var(--hover-color)]/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none" aria-hidden="true" />
                </motion.a>
              );
            })}
          </motion.div>
        </div>

        {/* HTML Sitemap for SEO */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="mb-8 pb-6 border-b border-border"
        >
          <h3 className="font-inter font-bold text-lg text-foreground mb-4 text-center">Site Navigation</h3>
          <nav aria-label="Site navigation">
            <div className="flex flex-wrap justify-center gap-4 md:gap-6">
              {sitemapLinks.map((link) => (
                <a
                  key={link.id}
                  href={`#${link.id}`}
                  className="font-inter font-medium text-sm text-foreground/80 hover:text-primary transition-colors duration-300 px-2 py-1 rounded"
                  onClick={(e) => {
                    e.preventDefault();
                    const element = document.getElementById(link.id);
                    if (element) {
                      const offset = 80;
                      const elementPosition = element.getBoundingClientRect().top;
                      const offsetPosition = elementPosition + window.pageYOffset - offset;
                      window.scrollTo({ top: offsetPosition, behavior: "smooth" });
                    }
                  }}
                  aria-label={`Navigate to ${link.label} section`}
                >
                  {link.label}
                </a>
              ))}
            </div>
          </nav>
        </motion.div>

        {/* Divider */}
        <div className="h-px bg-gradient-to-r from-transparent via-border to-transparent mb-8 mx-8" aria-hidden="true" />

        {/* Copyright and Info */}
        <div className="flex flex-col md:flex-row justify-between items-center">
          {/* Copyright Notice */}
          <div className="text-center md:text-left mb-6 md:mb-0">
            <p className="font-inter font-bold text-lg text-foreground mb-2">
              © {currentYear} Dumitrescu Photography
            </p>
            <p className="font-inter font-normal text-sm text-foreground/80">
              {t('footer.allRightsReserved')}
            </p>
          </div>

          {/* Contact Info */}
          <div className="text-center md:text-right">
            <div className="flex items-center justify-center md:justify-end mb-2">
              <MapPin className="w-4 h-4 text-foreground/80 mr-2" aria-hidden="true" />
              <p className="font-inter font-medium text-sm text-foreground">
                {t('footer.location')}
              </p>
            </div>
            <div className="flex items-center justify-center md:justify-end">
              <Mail className="w-4 h-4 text-foreground/80 mr-2" aria-hidden="true" />
              <a 
                href={`mailto:${t('footer.emailAddress')}`} 
                className="font-inter font-normal text-sm text-foreground/80 hover:text-primary transition-colors duration-300"
                aria-label="Send email to contact@dumitrescustudio.com"
              >
                {t('footer.emailAddress')}
              </a>
            </div>
          </div>
        </div>

        {/* Additional SEO Information */}
        <div className="mt-6 pt-4 border-t border-border">
          <div className="text-center">
            <p className="font-inter font-normal text-sm text-foreground/80 mb-2">
              Professional Photography Services in Sofia, Bulgaria
            </p>
            <div className="flex flex-wrap justify-center gap-3 text-sm text-foreground/80">
              <span>Editorial Photography</span>
              <span aria-hidden="true">•</span>
              <span>Architectural Photography</span>
              <span aria-hidden="true">•</span>
              <span>Portrait Photography</span>
              <span aria-hidden="true">•</span>
              <span>Commercial Photography</span>
              <span aria-hidden="true">•</span>
              <span>Fine Art Photography</span>
            </div>
          </div>
        </div>

        {/* Mobile Social Links */}
        <div className="mt-8 pt-6 border-t border-border md:hidden">
          <div className="flex justify-center space-x-8">
            {socialLinks.map((link) => {
              const Icon = footerIconMap[link.iconName as keyof typeof footerIconMap] || Camera;
              return (
                <a
                  key={link.name}
                  href={link.url}
                  target="_blank"
                  rel="noopener noreferrer nofollow"
                  className="flex flex-col items-center"
                  aria-label={`Visit ${link.name} profile`}
                >
                  <div className="w-12 h-12 rounded-full bg-card border border-border flex items-center justify-center mb-2 hover:border-[var(--hover-color)] transition-colors duration-300"
                    style={{ '--hover-color': link.color } as React.CSSProperties}
                  >
                    <Icon className="w-5 h-5 text-foreground" aria-hidden="true" />
                  </div>
                  <span className="font-inter font-medium text-sm text-foreground/80">
                    {link.name}
                  </span>
                </a>
              );
            })}
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;